/* Something to aim at, from minute one.
 *
 * The Learn home screen's empty state was a report of everything you hadn't
 * done — `0 XP · 🔥 0 days · ★ 0/110`, four grayed-out badges, then 22 courses.
 * That is true independent of rating or history, which is what makes it the
 * right companion to the onboarding wizard: everything here works with zero
 * games and zero account.
 *
 * The tone is the point. The app ASKS how often you want to train (F1, in the
 * wizard) rather than assigning a target, states an honest time cost before
 * you start ("12 cards, about 3 minutes") and then honors it, and defends a
 * streak instead of merely counting one. A streak with no defense is a
 * counter, not a mechanic.
 *
 * Pure: state in, numbers out. No catalog import, no React, no persistence.
 */

/* ---- F1 · the goal the user chooses ----
 *
 * Lives here rather than in onboarding.js because the wizard is one consumer
 * of it, not its owner — Settings and the home screen read the same list. */
export const GOAL_CHOICES = [
  { id: "casual", perWeek: 2, label: "Casual", hint: "2 days a week" },
  { id: "regular", perWeek: 4, label: "Regular", hint: "4 days a week" },
  { id: "serious", perWeek: 6, label: "Serious", hint: "6 days a week" },
];

/* DECIDED (2026-07-30, Pablo): someone who skips the question gets 4/week.
 * Changeable, and never punitive. */
export const DEFAULT_PER_WEEK = 4;

/* F2 · the honest time promise. Two knobs, both deliberately conservative.
 *
 * SECONDS_PER_CARD is measured against the drill's own pacing, not wishful:
 * a quiz answer plus the wrong-answer explanation window (which was
 * lengthened once already after "so fast I can't read it" was the top
 * gameplay complaint) lands around this. Overpromising speed is the one way a
 * time estimate can actively hurt — it turns a kept promise into a broken one.
 *
 * SESSION_CARDS caps the DAILY TARGET, never the deck. Someone returning from
 * a two-week break can have 80 cards due, and "80 cards, 16 minutes" is a wall
 * they will bounce off. The full pile stays one tap away ("N more due — do the
 * lot", on the TODAY panel); this is just what the app asks of them today. */
const SECONDS_PER_CARD = 12;
export const SESSION_CARDS = 15;

/* History is capped so the learn blob — which also carries Leitner state and
 * rides a single Firestore document — cannot grow without bound. Four months
 * is far more than the week strip needs and enough to look back on. */
export const MAX_HISTORY_DAYS = 120;

/* ---- days ----
 *
 * LOCAL dates, not UTC. `store.js` used `toISOString().slice(0, 10)` for the
 * streak, which rolls the day over at midnight UTC — 4pm for a user in UTC-8,
 * so their "day" flipped mid-afternoon. That is wrong in both directions:
 * two evening sessions on one calendar day could count as a two-day streak,
 * and a skipped day could go unnoticed. Fixed here rather than left alone
 * because F4 is about defending the streak, and there is no better moment —
 * there is no user base yet whose counters this would disturb. */
export function dayKey(ts = Date.now()) {
  const d = new Date(ts);
  const month = String(d.getMonth() + 1).padStart(2, "0");
  const day = String(d.getDate()).padStart(2, "0");
  return `${d.getFullYear()}-${month}-${day}`;
}

export const dayBefore = (key) => {
  const [y, m, d] = key.split("-").map(Number);
  return dayKey(new Date(y, m - 1, d - 1).getTime());
};

/* Monday-first, matching how the week strip reads in most of the world and
 * how "2 days left" is counted below. */
export function weekStart(ts = Date.now()) {
  const d = new Date(ts);
  d.setHours(0, 0, 0, 0);
  d.setDate(d.getDate() - ((d.getDay() + 6) % 7));
  return d;
}

export function goalOf(ls) {
  const g = ls?.goals || {};
  return {
    perWeek: Number.isFinite(g.perWeek) ? g.perWeek : DEFAULT_PER_WEEK,
    remindAt: g.remindAt || null,
    remindersOn: Boolean(g.remindersOn),
    history: g.history || {},
  };
}

/* Records that the user trained today, and how much. Called from the same
 * place as `bumpStreak` — one completed session.
 *
 * Adds rather than overwrites, because a second session on the same day is
 * more training, not a correction of the first. Mutates `state` in place,
 * matching bumpStreak/recordAnswer's convention (every caller is inside
 * LearnCenter's `mutate`, which clones first). */
export function recordTrainingDay(state, cards = 0, now = Date.now()) {
  const goals = state.goals || (state.goals = { updatedAt: now, perWeek: DEFAULT_PER_WEEK, remindAt: null, remindersOn: false });
  const history = goals.history || (goals.history = {});
  const key = dayKey(now);
  history[key] = (history[key] || 0) + Math.max(0, cards);
  /* W2d · and WHEN: one session per local hour bucket, so the reminder can
   * come before the user's usual session rather than at a time they never
   * train. Twenty-four keys at most, so the synced blob stays tiny. */
  const hours = goals.hours || (goals.hours = {});
  const hour = String(new Date(now).getHours());
  hours[hour] = (hours[hour] || 0) + 1;
  const keys = Object.keys(history).sort();
  for (const old of keys.slice(0, Math.max(0, keys.length - MAX_HISTORY_DAYS))) delete history[old];
  return history[key];
}

/* F3 · seven dots. `trained` is what fills one in; `isFuture` is what keeps a
 * day from reading as missed before it has happened — the difference between
 * "3 of 4 done, 2 days left" and "you have failed 4 times this week". */
/* Ids, not letters. Monday-first. This file is pure and has no business
 * holding copy — and a module constant would freeze the letters at whatever
 * locale was active when it first loaded. Today.jsx resolves them. */
const DAY_IDS = ["mon", "tue", "wed", "thu", "fri", "sat", "sun"];
export function weekStrip(ls, now = Date.now()) {
  const { history } = goalOf(ls);
  const start = weekStart(now);
  const today = dayKey(now);
  return DAY_IDS.map((day, i) => {
    const key = dayKey(new Date(start.getFullYear(), start.getMonth(), start.getDate() + i).getTime());
    return { key, day, trained: (history[key] || 0) > 0, isToday: key === today, isFuture: key > today };
  });
}

/* Days trained (and cards done) in the seven days starting at `startMs`.
 *
 * Lives here rather than in its two callers because `history` is this file's
 * shape: the weekly recap counts a finished week, the recap *notification*
 * counts the week it is about to be sent about, and both must agree or the
 * app will announce a card it does not then show. */
export function trainedDaysIn(ls, startMs) {
  const { history } = goalOf(ls);
  const from = new Date(startMs);
  let days = 0;
  let cards = 0;
  for (let i = 0; i < 7; i += 1) {
    const n = history[dayKey(new Date(from.getFullYear(), from.getMonth(), from.getDate() + i).getTime())] || 0;
    if (n > 0) {
      days += 1;
      cards += n;
    }
  }
  return { days, cards };
}

export function weekProgress(ls, now = Date.now()) {
  const strip = weekStrip(ls, now);
  const { perWeek } = goalOf(ls);
  const done = strip.filter((d) => d.trained).length;
  // Today counts as remaining until it is done — it is still winnable.
  const daysLeft = strip.filter((d) => (d.isFuture || d.isToday) && !d.trained).length;
  return { done, target: perWeek, daysLeft, met: done >= perWeek, trainedToday: strip.some((d) => d.isToday && d.trained) };
}

/* F2 · what the app asks of you today, and what it costs.
 *
 * `due` is the full due deck. The plan takes a slice of it — see SESSION_CARDS
 * — and reports the rest rather than hiding it, because a number the user can
 * see and a number the app quietly withheld are very different things. */
export function sessionPlan(due = [], ls = null, now = Date.now()) {
  const cards = due.slice(0, SESSION_CARDS);
  const minutes = Math.max(1, Math.round((cards.length * SECONDS_PER_CARD) / 60));
  return {
    cards,
    count: cards.length,
    overflow: Math.max(0, due.length - cards.length),
    minutes,
    done: cards.length === 0,
    trainedToday: weekProgress(ls, now).trainedToday,
  };
}

/* W1e · how far through today's session the user already is — the ring on the
 * session summary. Counted in cards against SESSION_CARDS, the same unit and
 * the same target the TODAY panel promises ("15 cards, about 3 minutes"), so
 * the summary never invents a second daily goal the home screen does not know
 * about. `history` is what `recordTrainingDay` writes, so a summary rendered
 * after finalize() already includes the session that just ended. */
export function todayProgress(ls, now = Date.now()) {
  const done = goalOf(ls).history[dayKey(now)] || 0;
  return { done, target: SESSION_CARDS, frac: Math.min(1, done / SESSION_CARDS), met: done >= SESSION_CARDS };
}

/* F4 · streak defense. The counter alone tells someone they are about to lose
 * fourteen days of investment only AFTER they have lost it.
 *
 * A banked freeze changes what "already broken" means, so it has to be a
 * third state, not a footnote on `lapsed`: someone who skipped exactly one
 * day and still has a freeze has not actually lost anything YET — training
 * today spends the freeze and the count continues; not training today is
 * what turns it into a real loss. Calling that `lapsed` would be exactly the
 * kind of guess this codebase refuses to make elsewhere (E3's "fixed" is
 * measured, never inferred) — the streak is not broken, it is one session
 * away from a decision. */
export function streakDefense(ls, now = Date.now()) {
  const count = ls?.streak?.count || 0;
  const last = ls?.streak?.lastDay || null;
  const freezes = ls?.streak?.freezes || 0;
  const today = dayKey(now);
  const yesterday = dayBefore(today);
  const safeToday = last === today;
  const oneDayMissed = last === dayBefore(yesterday);
  return {
    count,
    freezes,
    safeToday,
    /* At risk only if there is something to lose AND today would break it.
     * A streak of 0 cannot be defended, and saying so would be nagging. */
    atRisk: count > 0 && !safeToday && last === yesterday,
    /* One full day already went by, but a banked freeze covers it if the user
     * trains today. Distinct from `atRisk` (nothing has been missed yet) and
     * from `lapsed` (nothing left that can save it). */
    freezeWillCover: count > 0 && !safeToday && oneDayMissed && freezes > 0,
    /* Already broken — the streak will restart at 1 rather than continue.
     * True when more than one full day has passed, or exactly one has and
     * there is no freeze left to spend on it. */
    lapsed: count > 0 && !safeToday && last !== yesterday && !(oneDayMissed && freezes > 0),
  };
}

/* F4 · one freeze, earned by hitting the week's own goal — not automatic
 * every week, an actual reward for the behavior the app wants. Capped so it
 * cannot stockpile into a standing guarantee that training is optional; two
 * is enough to survive an ordinary bad week without turning the mechanic
 * into a second currency.
 *
 * Idempotent per week via `freezeWeek`: hitting the goal on day 4 and again
 * on day 6 of the same week grants exactly one, not two. Mutates `state` in
 * place, matching `bumpStreak`/`recordTrainingDay`'s convention — call it
 * from the same site, after `recordTrainingDay`, so this week's cards are
 * already counted when `weekProgress` checks whether the goal was met. */
export const FREEZE_CAP = 2;
export function grantWeeklyFreeze(state, now = Date.now()) {
  const week = weekProgress(state, now);
  const key = dayKey(weekStart(now).getTime());
  const streak = state.streak || (state.streak = { count: 0, lastDay: null, freezes: 0, freezeWeek: null });
  if (!week.met || streak.freezeWeek === key || (streak.freezes || 0) >= FREEZE_CAP) return false;
  streak.freezeWeek = key;
  streak.freezes = Math.min(FREEZE_CAP, (streak.freezes || 0) + 1);
  return true;
}

/* W2b · the streak lengths worth stopping for. Recorded once each in
 * `streak.milestones` (store.js#bumpStreak) so a milestone is a moment, not a
 * banner that returns every day the count happens to sit on the number. */
export const STREAK_MILESTONES = [7, 30, 100];

/* W2c · streak repair, the manual counterpart to F4's automatic freeze.
 *
 * A freeze covers a gap before you notice it; a repair is for the day you
 * come back and see the number is already gone. It is EARNED, not bought: the
 * offer is to finish today's session, and the streak continues from where it
 * stood instead of restarting at 1. Deliberately narrow — a recent lapse
 * (the last trained day at most REPAIR_WINDOW_DAYS ago) and one repair per
 * REPAIR_COOLDOWN_DAYS — because a repair that is always available turns the
 * streak back into a counter that means nothing. */
export const REPAIR_WINDOW_DAYS = 3;
export const REPAIR_COOLDOWN_DAYS = 30;
const REPAIR_DAY_MS = 24 * 60 * 60 * 1000;

export function repairOffer(ls, now = Date.now()) {
  const defense = streakDefense(ls, now);
  const last = ls?.streak?.lastDay;
  if (!defense.lapsed || !last) return null;
  let key = dayKey(now);
  let gap = 0;
  while (key > last && gap <= REPAIR_WINDOW_DAYS) {
    key = dayBefore(key);
    gap += 1;
  }
  if (key !== last || gap > REPAIR_WINDOW_DAYS) return null;
  const usedAt = ls.streak.repairUsedAt || 0;
  if (usedAt && now - usedAt < REPAIR_COOLDOWN_DAYS * REPAIR_DAY_MS) return null;
  return { count: defense.count, gap };
}

/* The user accepted the offer: today's finished session repairs the streak.
 * Mutates in place; returns whether anything was armed. */
export function armRepair(state, now = Date.now()) {
  if (!repairOffer(state, now)) return false;
  state.streak = { ...state.streak, repairArmed: dayKey(now) };
  return true;
}

/* W2d · the hour this user trains most, once there is enough to call it a
 * habit: at least USUAL_HOUR_MIN_TOTAL sessions recorded and USUAL_HOUR_MIN_AT
 * in the winning hour. Ties go to the EARLIER hour — a reminder that comes a
 * little early can still be acted on; one that comes after the session is
 * noise. */
export const USUAL_HOUR_MIN_TOTAL = 5;
export const USUAL_HOUR_MIN_AT = 3;
export function usualHour(ls) {
  const hours = ls?.goals?.hours || {};
  let total = 0;
  let best = null;
  for (let h = 0; h < 24; h += 1) {
    const n = hours[String(h)] || 0;
    total += n;
    if (n > 0 && (best === null || n > (hours[String(best)] || 0))) best = h;
  }
  if (best === null || total < USUAL_HOUR_MIN_TOTAL || (hours[String(best)] || 0) < USUAL_HOUR_MIN_AT) return null;
  return best;
}
